import React from 'react';
import {
  View,
  IconButton,
  Badge,
  Box,
  HStack,
  Text,
  Skeleton,
} from 'native-base';

import { UserAvatar, RandomButton, PointsTag, Ring } from '../../';

// Redux
import {
  useAppSelector,
  useAppDispatch,
  toggleClientProfilePanel,
  toggleChatPanel,
  getChatsPanelState,
  getProfilePanelState,
  openModal,
} from '@breakroom-nx/redux';

import { User } from '@breakroom-nx/classes';

// Icons
import { ChatAlt2Icon, UserIcon } from 'react-native-heroicons/solid';

// Assets
import { theme } from '@breakroom-nx/assets';

export enum TabBarButtonType {
  Chat,
  Randomizer,
  Profile,
}
export interface TabBarButtonProps {
  type: TabBarButtonType;
  user?: User;
  badgeValue?: number;
  isAnonymous?: boolean;
  isLoading: boolean;
}

export function TabBarButton({
  type,
  isLoading,
  user = undefined,
  badgeValue = 0,
  isAnonymous = true,
}: TabBarButtonProps) {
  const dispatch = useAppDispatch();

  //* Component will be rerendered regardless if Redux or local state is used -> Irrelevant
  const chatsDisplayed = useAppSelector(getChatsPanelState);
  const profileDisplayed = useAppSelector(getProfilePanelState);

  const chatButton = (
    <Ring hidden={!chatsDisplayed}>
      <IconButton
        onPress={() => {
          dispatch(toggleChatPanel());
        }}
        bg={'rgba(255, 255, 255, 0.07)'}
        borderRadius={'full'}
        icon={<ChatAlt2Icon color={'rgba(255, 255, 255, 1)'} size={30} />}
        _pressed={{
          backgroundColor: chatsDisplayed
            ? theme.colors.primary[400]
            : 'rgba(255, 255, 255, 0.07)',
          // opacity: 0.7,
        }}
      />
    </Ring>
  );

  const randomizerButton = (
    <Ring>
      <IconButton
        bg={'transparent'}
        icon={<RandomButton />}
        borderRadius={'full'}
        _pressed={{
          backgroundColor: 'rgba(255, 255, 255, 0.07)',
          opacity: 0.7,
        }}
      />
    </Ring>
  );

  const profileButton = (
    <Ring hidden={!profileDisplayed}>
      <IconButton
        onPress={() => {
          isAnonymous
            ? dispatch(openModal({ modalType: 'AccountConversion' }))
            : dispatch(toggleClientProfilePanel());
        }}
        borderRadius={'full'}
        justifyContent={'center'}
        icon={
          user ? (
            <UserAvatar
              user={user}
              disabled={true}
              noBorder
              noMargin
              uid={user.uid}
              size={50}
            />
          ) : (
            <UserIcon
              color={
                profileDisplayed ? theme.colors.white : 'rgba(255, 255, 255, 1)'
              }
              size={30}
            />
          )
        }
      />
    </Ring>
  );

  const getButton = () => {
    switch (type) {
      case TabBarButtonType.Chat:
        return chatButton;
      case TabBarButtonType.Randomizer:
        return randomizerButton;
      case TabBarButtonType.Profile:
        return profileButton;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Skeleton
        rounded={'full'}
        startColor={'surface.200'}
        endColor={'surface.500'}
        size={'70px'}
      />
    );
  }

  return (
    <HStack alignItems={'center'}>
      <View>
        {getButton()}

        {type !== TabBarButtonType.Randomizer ? (
          <Box position={'absolute'} zIndex={9999} top={'0px'} right={'-5px'}>
            {badgeValue > 0 ? (
              <Badge
                bg={'red.200'}
                rounded={'full'}
                alignItems={'center'}
                justifyContent={'center'}
                colorScheme={'white'}
              >
                <Text fontSize={12} lineHeight={16}>
                  {badgeValue}
                </Text>
              </Badge>
            ) : null}
          </Box>
        ) : null}

        {type === TabBarButtonType.Profile && !isAnonymous ? (
          <Box
            rounded={'full'}
            position={'absolute'}
            bottom={'-1rem'}
            style={{
              shadowColor: 'black',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.2,
              shadowRadius: 5,
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderWidth: 0,
            }}
          >
            <PointsTag
              amount={user?.points ?? 0}
              payment={false}
              fontSize={16}
              imageSize={15}
              px={3}
            />
          </Box>
        ) : null}
      </View>
    </HStack>
  );
}

export default React.memo(TabBarButton);
